import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@akuna/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check } from "lucide-react";

/**
 * Match when every whitespace-separated token appears somewhere in the label (case-insensitive).
 * e.g. "Tosa Basset" matches "Tosa higiénica Basset".
 */
function optionMatchesAutocompleteSearch(label: string, search: string): boolean {
  const normalizedLabel = label.toLowerCase();
  const tokens = search
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every((token) => normalizedLabel.includes(token));
}

export interface AutocompleteOption {
  label: string;
  value: string;
}

interface AutocompleteProps {
  options: AutocompleteOption[];
  value: string;
  onChange: (value: string) => void;
  onSelect?: (option: AutocompleteOption) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  emptyMessage?: string | React.ReactNode;
  disabled?: boolean;
  className?: string;
  renderOption?: (option: AutocompleteOption, isSelected: boolean) => React.ReactNode;
  selectedValue?: string; // Valor atualmente selecionado (para calcular isSelected)
  disableLocalFilter?: boolean; // Se true, não filtra localmente (assume busca externa)
  footer?: React.ReactNode; // Conteúdo customizado no final da lista de opções
  /**
   * Deixe `false` quando o autocomplete vive dentro de um Dialog/Sheet modal.
   *
   * O Dialog do Radix trava o scroll com `RemoveScroll` liberando apenas a
   * subárvore do próprio content (`shards: [contentRef]`). Uma lista portalada
   * no `document.body` fica fora dessa subárvore e simplesmente não rola.
   *
   * Não remova esses `popoverPortalled={false}` achando que são resíduo do bug
   * de Radix duplicado (corrigido em `packages/akuna-ui/vite.config.ts`): são
   * coisas diferentes. O preço é a lista poder ser recortada pelo `overflow` do
   * diálogo — trade-off aceito conscientemente em favor do scroll.
   */
  popoverPortalled?: boolean;
  dropdownClassName?: string;
  listClassName?: string;
  optionClassName?: string;
  openOnFocus?: boolean;
  openOnClick?: boolean;
  contentWidth?: "trigger" | "content";
  usePopoverAnchor?: boolean;
  preventParentScrollOnFocus?: boolean;
  useInlineDropdown?: boolean;
  id?: string;
  ariaLabel?: string;
  keepOpenOnSelect?: boolean;
}

export const Autocomplete = React.forwardRef<HTMLInputElement, AutocompleteProps>(
  (
    {
      options,
      value,
      onChange,
      onSelect,
      onBlur,
      onFocus,
      placeholder = "Type to search...",
      emptyMessage = "No results found.",
      disabled = false,
      className,
      renderOption,
      selectedValue: externalSelectedValue,
      disableLocalFilter = false,
      footer,
      popoverPortalled = true,
      dropdownClassName,
      listClassName,
      optionClassName,
      openOnFocus = true,
      openOnClick = true,
      contentWidth = "trigger",
      usePopoverAnchor = false,
      preventParentScrollOnFocus = false,
      useInlineDropdown = false,
      id,
      ariaLabel,
      keepOpenOnSelect = false,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [internalSelectedValue, setInternalSelectedValue] = React.useState<string>("");
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popoverContentRef = React.useRef<HTMLDivElement>(null);
    const isMobile = useIsMobile();

    // Always close when the user presses or focuses anything outside this field and its list,
    // so two fields in the same form never keep their dropdowns open at the same time.
    React.useEffect(() => {
      if (!open) return;
      const isInside = (target: EventTarget | null) =>
        target instanceof Node &&
        Boolean(containerRef.current?.contains(target) || popoverContentRef.current?.contains(target));
      const handlePointerDown = (event: PointerEvent) => {
        if (!isInside(event.target)) setOpen(false);
      };
      const handleFocusIn = (event: FocusEvent) => {
        if (!isInside(event.target)) setOpen(false);
      };
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("focusin", handleFocusIn);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("focusin", handleFocusIn);
      };
    }, [open]);

    // Use external selectedValue if provided, otherwise use internal state
    const selectedValue = externalSelectedValue ?? internalSelectedValue;

    // Filter options based on input value
    const filteredOptions = React.useMemo(() => {
      // Se filtro local está desabilitado, retornar todas as opções
      // (assumindo que o componente pai já fez a busca)
      if (disableLocalFilter) {
        return options;
      }

      if (!value || value.trim() === "") {
        return options;
      }
      return options.filter((option) => optionMatchesAutocompleteSearch(option.label, value));
    }, [options, value, disableLocalFilter]);

    // Handle option selection
    const handleSelect = (option: AutocompleteOption) => {
      setInternalSelectedValue(option.value);
      onChange(option.label);
      setOpen(keepOpenOnSelect);
      onSelect?.(option);
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      setInternalSelectedValue("");

      // Open popover when user types
      if (!open && newValue.length > 0) {
        setOpen(true);
      }
    };

    // Handle input focus
    const handleInputFocus = () => {
      onFocus?.();
      if (openOnFocus) {
        setOpen(true);
      }
    };

    // Handle input click - prevent PopoverTrigger from toggling
    const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
      e.stopPropagation(); // Prevent event from reaching PopoverTrigger
      if (openOnClick) {
        setOpen(true);
      }
    };

    const handleInputMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
      if (!preventParentScrollOnFocus) return;
      // Keep focus on input without letting browser scroll parent containers.
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.focus({ preventScroll: true });
      if (openOnClick) {
        setOpen(true);
      }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
      // Let Command component handle ArrowDown, ArrowUp, and Enter
    };

    const renderCommandItems = () =>
      filteredOptions.map((option) => {
        const isSelected = selectedValue === option.value;
        return (
          <CommandItem
            key={option.value}
            value={option.value}
            onSelect={() => handleSelect(option)}
            className={cn(renderOption ? "items-start py-2" : "items-start", optionClassName)}
          >
            {renderOption ? (
              renderOption(option, isSelected)
            ) : (
              <>
                <Check
                  className={cn(
                    "mr-2 mt-0.5 h-4 w-4 shrink-0",
                    isSelected ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="min-w-0 flex-1 whitespace-normal break-words text-left">
                  {option.label}
                </span>
              </>
            )}
          </CommandItem>
        );
      });

    const TriggerWrapper = usePopoverAnchor ? PopoverAnchor : PopoverTrigger;
    const shouldRenderInlineDropdown = useInlineDropdown && !popoverPortalled;

    React.useEffect(() => {
      if (!shouldRenderInlineDropdown || !open) return;

      const handlePointerDownOutside = (event: MouseEvent | TouchEvent) => {
        if (!containerRef.current) return;
        const target = event.target as Node | null;
        if (target && containerRef.current.contains(target)) return;
        setOpen(false);
      };

      document.addEventListener("mousedown", handlePointerDownOutside);
      document.addEventListener("touchstart", handlePointerDownOutside);
      return () => {
        document.removeEventListener("mousedown", handlePointerDownOutside);
        document.removeEventListener("touchstart", handlePointerDownOutside);
      };
    }, [open, shouldRenderInlineDropdown]);

    if (isMobile) {
      return (
        <>
          <Input
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            type="text"
            value={value}
            readOnly
            inputMode="none"
            onClick={() => {
              if (!disabled) setOpen(true);
            }}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(className)}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
          />
          <Drawer
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) onBlur?.();
            }}
            shouldScaleBackground={false}
          >
            <DrawerContent
              ref={popoverContentRef}
              className="h-[85dvh]"
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              <DrawerTitle className="sr-only">{placeholder}</DrawerTitle>
              <div className="border-b border-border p-3">
                <Input
                  type="text"
                  value={value}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  aria-label={placeholder}
                />
              </div>
              <Command shouldFilter={false} className="min-h-0 flex-1 bg-transparent">
                <CommandList className="max-h-none flex-1 overflow-y-auto">
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>{renderCommandItems()}</CommandGroup>
                </CommandList>
                {footer && (
                  <div className="border-t bg-popover px-2 py-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]">
                    {footer}
                  </div>
                )}
              </Command>
            </DrawerContent>
          </Drawer>
        </>
      );
    }

    if (shouldRenderInlineDropdown) {
      return (
        <div ref={containerRef} className="relative">
          <Input
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            type="text"
            value={value}
            onChange={handleInputChange}
            onMouseDown={handleInputMouseDown}
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={cn(className)}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
          />
          {open ? (
            <div
              className={cn(
                "absolute left-0 right-0 top-full z-50 mt-1 rounded-md border bg-popover p-0 text-popover-foreground shadow-md",
                dropdownClassName,
              )}
            >
              <Command shouldFilter={false}>
                <CommandList
                  className={cn("max-h-72 overflow-y-auto overflow-x-visible", listClassName)}
                >
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>{renderCommandItems()}</CommandGroup>
                </CommandList>
                {footer && (
                  <div className="border-t bg-popover px-2 py-1.5">
                    {footer}
                  </div>
                )}
              </Command>
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <TriggerWrapper asChild>
          <div className="relative" ref={containerRef}>
            <Input
              ref={ref}
              id={id}
              aria-label={ariaLabel}
              type="text"
              value={value}
              onChange={handleInputChange}
              onMouseDown={handleInputMouseDown}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(className)}
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
            />
          </div>
        </TriggerWrapper>
        <PopoverContent
          ref={popoverContentRef}
          className={cn(
            "p-0 max-w-[min(90vw,48rem)]",
            contentWidth === "content"
              ? "w-max min-w-[var(--radix-popover-trigger-width)]"
              : "w-[var(--radix-popover-trigger-width)]",
            dropdownClassName,
          )}
          align="start"
          portalled={popoverPortalled}
          onOpenAutoFocus={(e) => {
            // Prevent popover from stealing focus from input
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // Avoid Radix trying to refocus trigger/anchor and scrolling parent containers.
            e.preventDefault();
          }}
        >
          <Command shouldFilter={false}>
            <CommandList
              className={cn(
                "max-h-[min(18rem,calc(var(--radix-popover-content-available-height,21rem)_-_3rem))] overflow-y-auto overflow-x-visible",
                listClassName,
              )}
            >
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>{renderCommandItems()}</CommandGroup>
            </CommandList>
            {footer && (
              <div className="border-t bg-popover px-2 py-1.5">
                {footer}
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

Autocomplete.displayName = "Autocomplete";

export interface MultipleAutocompleteProps {
  options: AutocompleteOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  emptyMessage: string | React.ReactNode;
  removeLabel: string;
  disabled?: boolean;
  renderOption?: (option: AutocompleteOption, isSelected: boolean) => React.ReactNode;
  renderSelectedOption?: (option: AutocompleteOption) => React.ReactNode;
  popoverPortalled?: boolean;
  listClassName?: string;
  optionClassName?: string;
  ariaLabel?: string;
}

/** Autocomplete com seleção múltipla e chips removíveis para as opções escolhidas. */
export function MultipleAutocomplete({
  options,
  values,
  onChange,
  placeholder,
  emptyMessage,
  removeLabel,
  disabled = false,
  renderOption,
  renderSelectedOption,
  popoverPortalled = true,
  listClassName,
  optionClassName,
  ariaLabel,
}: MultipleAutocompleteProps) {
  const [search, setSearch] = React.useState("");
  const selectedOptions = options.filter((option) => values.includes(option.value));

  const toggleOption = (option: AutocompleteOption) => {
    const nextValues = values.includes(option.value)
      ? values.filter((value) => value !== option.value)
      : [...new Set([...values, option.value])];
    onChange(nextValues);
    setSearch("");
  };

  return (
    <div className="space-y-2">
      <Autocomplete
        options={options}
        value={search}
        onChange={setSearch}
        onSelect={toggleOption}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
        disabled={disabled}
        ariaLabel={ariaLabel}
        selectedValue=""
        keepOpenOnSelect
        popoverPortalled={popoverPortalled}
        listClassName={listClassName}
        optionClassName={optionClassName}
        renderOption={(option) => renderOption?.(option, values.includes(option.value)) ?? (
          <>
            <Check
              className={cn("mr-2 mt-0.5 h-4 w-4 shrink-0", values.includes(option.value) ? "opacity-100" : "opacity-0")}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 whitespace-normal break-words text-left">{option.label}</span>
          </>
        )}
      />
      {selectedOptions.length > 0 ? (
        <div className="flex flex-wrap gap-2" aria-label={placeholder}>
          {selectedOptions.map((option) => (
            <span key={option.value} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground">
              {renderSelectedOption ? renderSelectedOption(option) : <span className="min-w-0 truncate">{option.label}</span>}
              <button
                type="button"
                className="rounded-full p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`${removeLabel}: ${option.label}`}
                disabled={disabled}
                onClick={() => toggleOption(option)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
